import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mlm/ui';
import { useCartStore } from '../storefront/lib/cart-store';
import { productToAddLine } from '../storefront/lib/cart-map';
import { toDisplay, useBrand } from '../storefront/lib/hooks';
import { ProductCard } from './product-card';
import { useActivationKits } from '../lib/hooks/use-activation-kits';

/**
 * `content-start` is load-bearing: it neutralises `align-content: stretch`, so a
 * caller that constrains this grid's height can never squash the rows (and their
 * `overflow-hidden` cards) instead of scrolling. The grid takes no className
 * prop for the same reason — a scroll cap belongs on a wrapper, never here. See
 * the comment in `kit-picker-dialog.tsx`.
 */
const GRID = 'grid grid-cols-1 content-start gap-3 sm:grid-cols-2 lg:grid-cols-3';

export function KitGrid({
  enabled = true,
  onPicked,
}: {
  /** Gate the queries (e.g. only while the hosting dialog is open). */
  enabled?: boolean;
  /** Called when Buy Now hands off to /checkout (dialogs close themselves). */
  onPicked?: () => void;
}) {
  const navigate = useNavigate();
  const brand = useBrand();
  const lines = useCartStore((s) => s.lines);
  const addToCart = useCartStore((s) => s.add);
  const addOnly = useCartStore((s) => s.addOnly);
  const { kits, allKits, isLoading } = useActivationKits(enabled);

  // Normalized rows for the cart mapper, keyed by id so the card's own raw row
  // still drives the display. Kits can never have variations
  // (`kit_cannot_have_variants`), so a kit line needs no variant id.
  const displayById = useMemo(() => new Map(allKits.map((p) => [p.id, toDisplay(p)])), [allKits]);
  const inCartQty = (productId: string) =>
    Object.values(lines).find((l) => l.productId === productId)?.qty ?? 0;
  const addKit = (productId: string) => {
    const d = displayById.get(productId);
    if (d) addToCart(productToAddLine(d, brand, { qty: 1 }));
  };

  function buyNow(productId: string) {
    const d = displayById.get(productId);
    if (!d) return;
    // `addOnly` rather than `add`: this is a one-thing purchase, so whatever is
    // already in the cart must not be billed alongside the activation kit.
    addOnly(productToAddLine(d, brand, { qty: 1 }));
    onPicked?.();
    // Always /checkout. A VIRTUAL kit gets the instant-pay treatment from there
    // — checkout redirects an all-virtual cart to the payment page itself — so
    // this must NOT special-case it. Deciding here as well would be a second
    // copy of that rule, and a PHYSICAL kit sent straight to payment would skip
    // the address or pickup branch it still needs.
    navigate('/checkout');
  }

  if (isLoading) {
    return (
      <div className={GRID}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-56" />
        ))}
      </div>
    );
  }

  if (kits.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No {brand.activationKitLabelPlural} are available yet. Please check back, or contact your
        sponsor.
      </p>
    );
  }

  return (
    <div className={GRID}>
      {kits.map((p) => (
        <ProductCard
          key={p.id}
          view={{
            id: p.id,
            name: p.name,
            sku: p.sku,
            description: p.description,
            images: p.images,
            isKit: p.isKit,
            featured: p.featured,
            pv: p.pv,
            price: p.priceEffective,
            priceBefore: p.priceBase,
            discountPct: Number(p.appliedDiscountPct),
          }}
          basePath="/product"
          inCart={inCartQty(p.id)}
          onAdd={() => addKit(p.id)}
          onBuyNow={() => buyNow(p.id)}
        />
      ))}
    </div>
  );
}
